import { ReturnTypeEnum } from "@redotech/redo-model/return";
import {
  folderTypes,
  FolderTypesNames,
} from "@redotech/redo-model/views/folders";
import { ViewTypesNames, WarrantyView } from "@redotech/redo-model/views/views";
import { createContext, memo } from "react";
import { CreateReturnViewFolderModal } from "../../return/views/create-return-view-folder-modal";
import { EditReturnViewFolderModal } from "../../return/views/edit-return-view-folder-modal";
import { useViewOrderingService } from "../shared/view-ordering-service-base";
import {
  ReloadWarrantyViewsContext,
  WarrantyViewsContext,
} from "./warranty-views";

export interface WarrantyViewOrderingData {
  openCreateFolderModal: () => void;
  openEditFolderModal: (folderId: string) => void;
  handleDeleteFolder: (folderId: string) => void;
  updateOrdering: (
    updatedViews: WarrantyView[],
    updatedFolders: folderTypes[FolderTypesNames.WARRANTIES][],
  ) => void;
  saveOrdering: () => void;
  views: WarrantyView[];
  folders: folderTypes[FolderTypesNames.WARRANTIES][];
  notificationCounts: Record<string, number>;
}

export const WarrantyViewOrderingContext = createContext<
  WarrantyViewOrderingData | undefined
>(undefined);

export const WarrantyViewOrderingService = memo(
  function WarrantyViewOrderingService({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const {
      views,
      folders,
      notificationCounts,
      showCreateFolderModal,
      showEditFolderModal,
      selectedFolder,
      reservedFolderNames,
      handleCreateFolder,
      handleEditFolder,
      handleDeleteFolder,
      updateOrdering,
      saveOrdering,
      openCreateFolderModal,
      openEditFolderModal,
      setShowCreateFolderModal,
      setShowEditFolderModal,
    } = useViewOrderingService({
      returnType: ReturnTypeEnum.WARRANTY,
      viewType: ViewTypesNames.WARRANTIES,
      navDataContext: WarrantyViewsContext,
      reloadNavDataContext: ReloadWarrantyViewsContext,
    });

    return (
      <WarrantyViewOrderingContext.Provider
        value={{
          openCreateFolderModal,
          openEditFolderModal,
          handleDeleteFolder,
          updateOrdering,
          saveOrdering,
          views,
          folders,
          notificationCounts,
        }}
      >
        {children}
        <CreateReturnViewFolderModal
          isOpen={showCreateFolderModal}
          onClose={() => setShowCreateFolderModal(false)}
          onSubmit={handleCreateFolder}
          reservedFolderNames={reservedFolderNames}
        />
        {selectedFolder && (
          <EditReturnViewFolderModal
            deleteFolder={handleDeleteFolder}
            isOpen={showEditFolderModal}
            onClose={() => setShowEditFolderModal(false)}
            onSubmit={handleEditFolder}
            reservedFolderNames={reservedFolderNames}
            selectedFolder={selectedFolder}
          />
        )}
      </WarrantyViewOrderingContext.Provider>
    );
  },
);
